import { useState } from "react";
import { Link } from "react-router";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Button from "../ui/button/Button";
import { apiPost } from "../../lib/api";

export default function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const frontendBaseUrl =
    (import.meta.env.VITE_FRONTEND_BASE_URL as string | undefined)?.replace(
      /\/+$/,
      "",
    ) || window.location.origin;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    setError(null);
    setSubmitting(true);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Please enter your email address.");
      setSubmitting(false);
      return;
    }

    try {
      const response = await apiPost<{ message: string }>("/password/email", {
        email: trimmedEmail,
        // Some backends support overriding the reset link base URL via redirect_url.
        // If ignored, it is harmless.
        redirect_url: `${frontendBaseUrl}/reset-password`,
      });
      setMessage(
        response.message ||
          "Password reset link sent. Please check your email.",
      );
    } catch (err: any) {
      const apiMessage = err?.data?.message || err?.data?.errors?.email?.[0];
      const friendlyMessage = apiMessage
        ? apiMessage
        : "Failed to send reset link. Please try again.";
      setError(friendlyMessage);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative w-full flex flex-col items-center py-6 px-4 dark:bg-[#080f1c]">
      {/* relative w-full min-h-screen flex items-center justify-center py-16 px-4 */}
      {/* Top grid */}
      <img
        src="/images/shape/grid-01.svg"
        alt=""
        aria-hidden="true"
        className="pointer-events-none fixed right-0 top-0 w-[450px] xl:w-[750px] opacity-80 dark:opacity-90 dark:[filter:brightness(0)_invert(1)]"
        style={{ zIndex: 0 }}
      />
      {/* Bottom grid */}
      <img
        src="/images/shape/grid-01.svg"
        alt=""
        aria-hidden="true"
        className="pointer-events-none fixed left-0 bottom-0 w-[450px] xl:w-[750px] rotate-180 opacity-80 dark:opacity-90 dark:[filter:brightness(0)_invert(1)]"
        style={{ zIndex: 0 }}
      />
      <div className="flex justify-center sm:mt-10 sm:mb-10 mt-2 mb-2">
        <Link to="/" className="dark:hidden">
          <img
            className="w-[120px] h-auto sm:w-[200px] sm:h-[42px]"
            src="/images/logo/netsight-Black.svg"
            alt="Logo"
          />
        </Link>
        <Link to="/" className="hidden dark:block">
          <img
            className="w-[120px] h-auto sm:w-[200px] sm:h-[42px]"
            src="/images/logo/netsight-white.svg"
            alt="Logo"
          />
        </Link>
      </div>
      {/* <div className="sm:mt-10 sm:mb-10 mt-3 mb-3 text-center">
        <Link
          to="/"
          className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
        >
          ← Back to sign in
        </Link>
      </div> */}
      {/* Compact form card */}
      <div
        className="relative w-full max-w-md bg-white dark:bg-[#0d1828] rounded-2xl shadow-theme-lg p-8 border border-gray-100 dark:border-[#FFFFFF0F] shadow-theme-lg"
        style={{ zIndex: 3 }}
      >
        <div className="mb-5">
          <h1 className=" font-semibold text-gray-800 dark:text-white/90 text-xl text-center">
            Forgot Password
          </h1>
          <p className="text-sm font-normal text-center text-gray-500 dark:text-gray-400">
            Enter the email address associated with your account and we&apos;ll
            send you a password reset link.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {message && (
            <div className="p-3 text-sm text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800">
              {message}
            </div>
          )}
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
              {error}
            </div>
          )}
          <div>
            <Label>
              Email <span className="text-error-500">*</span>
            </Label>
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <button
              className="flex items-center justify-center w-full px-6 py-2 text-sm font-medium text-white transition rounded-[6px] bg-teal-600 hover:bg-teal-700 disabled:opacity-60 -mt-2"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Sending reset link..." : "Send reset link"}
            </button>
          </div>
        </form>
      </div>
      <div className="sm:mt-10 sm:mb-10 mt-3 mb-3">
        <p className="text-sm font-normal text-center text-gray-500 dark:text-gray-400">
          Remembered your password?{" "}
          <Link
            to="/signin"
            className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
          >
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
